// Fluency.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Card, CardHeader, CardContent, CircularProgress, Alert, IconButton, Switch, FormControlLabel } from '@material-ui/core';
import FullscreenIcon from '@material-ui/icons/Fullscreen'; // Diagonal arrows pointing outward
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit'; // Arrows pointing inward
import { useDashboardStyles } from './dashboardStyles';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Debug Logging
const DEBUG_MODE = false;
const log = (...args) => {
  if (DEBUG_MODE) {
    console.log('[Fluency Component]', ...args);
  }
};

// Constants for the component
const fluencyLineColor = '#4BC0C0'; // Teal line for fluency
const headerColorFromMockup = '#0087B6';  // Updated from #00BBEB

// Color constants for fluency bands with transparency - use same colors as Skip Rate/Accuracy
const chartBandGreen = 'rgba(217, 234, 218, 0.6)';  // Light green for high fluency (>99)
const chartBandBlue = 'rgba(226, 239, 180, 0.6)';   // Light blue for medium fluency (80-99)
const chartBandYellow = 'rgba(233, 188, 145, 0.6)'; // Light yellow/orange for low fluency (<80)

// Threshold values for fluency - using absolute numbers, not percentages
const thresholdGreenLowerLimit = 100;  // Green: >150 (Good fluency)
const thresholdBlueLowerLimit = 50;   // Blue: 50-149 (Medium fluency)
const thresholdYellowUpperLimit = 50; // Yellow: <50 (Poor fluency)

const Fluency = ({ data, loading, error, books = [], student = {}, isExpanded = false, isCollapsed = false, onExpand }) => {
  const chartContainerRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const classes = useDashboardStyles();
  
  // State for level filtering - default to true (on)
  const [showLeveledData, setShowLeveledData] = useState(true);
  
  // Helper function to get book title by ID
  const getBookTitle = (bookId) => {
    if (!bookId) return 'Unknown Book';
    
    const book = books.find(b => b.id === bookId || b.id === String(bookId));
    if (!book || !book.name) return `Book ID: ${bookId}`;
    
    // Construct title with level from book object
    let title = book.name;
    if (book.bookLevel) {
      title = `${title} (${book.bookLevel})`;
    }
    
    // Truncate title if too long (over 50 characters to account for level)
    return title.length > 50 ? `${title.substring(0, 47)}...` : title;
  };

  // Format date to MM/DD format
  const formatDate = (dateString) => {
    try {
      // Handle the date format "2025-05-15-04:00"
      // First replace the last hyphen with a space to make it ISO compatible
      const formattedDateString = dateString.replace(/(\d{4}-\d{2}-\d{2})-(\d{2}:\d{2})/, '$1T$2');
      const date = new Date(formattedDateString);
      
      if (isNaN(date.getTime())) {
        log('Invalid date:', dateString);
        return 'Invalid date';
      }
      
      return `${date.getMonth() + 1}/${date.getDate()}`;
    } catch (e) {
      log('Error formatting date:', e, dateString);
      return 'Invalid date';
    }
  };

  // Filter data based on level setting
  const filterDataByLevel = (data) => {
    // Only filter if dashboard is enabled, showLeveledData is true, and we have a current level
    if (student.isDashboardEnabled === 'Y' && showLeveledData && student.currentBuddyBooksLevel) {
      return data.filter(item => {
        if (!item.bookId) return false;
        const book = books.find(b => b.id === item.bookId || b.id === String(item.bookId));
        return book && book.bookLevel === student.currentBuddyBooksLevel;
      });
    }
    return data;
  };

  // Process fluency data for the chart
  const processFluencyData = (fluencyData) => {
    if (!fluencyData || fluencyData.length === 0) {
      log('No fluency data available');
      return { labels: [], values: [], original: [] };
    }
    
    // Filter out any malformed data entries
    let validData = fluencyData.filter(item => {
      // Check if we have both fluency and date as expected
      return (
        typeof item === 'object' && 
        item !== null && 
        'fluency' in item && 
        'date' in item
      );
    });
    
    // Apply level filtering if enabled
    validData = filterDataByLevel(validData);
    
    if (validData.length === 0) {
      log('No valid data entries after filtering');
      return { labels: [], values: [], original: [] };
    }
    
    log('Valid data entries:', validData.length);
    
    // Group by date and calculate weighted averages
    const groupedByDate = {};
    
    validData.forEach(item => {
      const date = item.date;
      if (!groupedByDate[date]) {
        groupedByDate[date] = {
          date,
          items: [],
          totalWeight: 0,
          weightedSum: 0
        };
      }
      
      const entryCount = item.entryCount || 1; // Default to 1 if not provided
      const fluency = item.fluency;
      
      groupedByDate[date].items.push(item);
      groupedByDate[date].totalWeight += entryCount;
      groupedByDate[date].weightedSum += fluency * entryCount;
    });
    
    // Calculate weighted averages and create aggregated data
    const aggregatedData = Object.values(groupedByDate).map(group => ({
      date: group.date,
      fluency: group.weightedSum / group.totalWeight,
      originalItems: group.items,
      entryCount: group.totalWeight
    }));
    
    // Sort data by date - improve date parsing for consistency
    const sortedData = [...aggregatedData].sort((a, b) => {
      try {
        // Convert date strings to standardized format for comparison
        const dateA = a.date.replace(/(\d{4}-\d{2}-\d{2})-(\d{2}:\d{2})/, '$1T$2');
        const dateB = b.date.replace(/(\d{4}-\d{2}-\d{2})-(\d{2}:\d{2})/, '$1T$2');
        
        return new Date(dateA) - new Date(dateB);
      } catch (e) {
        log('Error sorting dates:', e);
        return 0;
      }
    });

    // Format dates for display (MM/DD format)
    const labels = sortedData.map(item => formatDate(item.date));

    // Use the actual fluency values from the API data
    // Round to nearest whole number and cap at 200
    const values = sortedData.map(item => {
      // Round to whole number
      const roundedValue = Math.round(item.fluency);
      // Cap at 200 for display purposes
      return Math.min(roundedValue, 200);
    });

    log('Processed fluency data', { labels, values });
    return { labels, values, original: sortedData };
  };

  // Calculate average fluency
  const getAverageFluency = () => {
    if (!data || data.length === 0) return null;
    
    // Filter valid fluency entries
    const validEntries = data.filter(item => 
      typeof item === 'object' && 
      item !== null && 
      'fluency' in item &&
      !isNaN(item.fluency)
    );
    
    if (validEntries.length === 0) return null;
    
    // Calculate average from raw fluency values
    const total = validEntries.reduce((sum, item) => sum + Number(item.fluency), 0);
    // Round to nearest whole number
    return Math.round(total / validEntries.length);
  };

  // Destroy chart when component unmounts or data changes
  useEffect(() => {
    return () => {
      if (chartInstanceRef.current) {
        log('Destroying chart instance');
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };
  }, []);

  // Prepare chart data
  const chartData = processFluencyData(data);
  log('Chart data', chartData);
  
  // Chart options
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    clip: false,
    animation: {
      duration: 500
    },
    layout: {
      padding: {
        top: 20,
        bottom: 15,
        left: 10,
        right: 10
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 200,
        ticks: {
          stepSize: 50,
          callback: function(value) {
            return value;
          }
        },
        grid: {
          color: 'rgba(200, 200, 200, 0.3)'
        }
      },
      x: {
        grid: {
          color: 'rgba(200, 200, 200, 0.3)'
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
          autoSkip: true,
          maxTicksLimit: isExpanded ? 15 : 5,
          padding: 5,
          font: {
            size: 11
          }
        }
      }
    },
    plugins: {
      legend: {
        display: false
      },
      title: {
        display: false
      },
      tooltip: {
        position: 'nearest',
        mode: 'point',
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        padding: 10,
        displayColors: false,
        callbacks: {
          label: function(context) {
            const dataIndex = context.dataIndex;
            const originalData = chartData.original[dataIndex];
            const originalItems = originalData && originalData.originalItems || [];
            
            if (originalItems.length === 1) {
              // Single datapoint
              const item = originalItems[0];
              const bookId = item.bookId || 'N/A';
              const fluency = item.fluency;
              
              let displayFluency;
              if (fluency > 200) {
                displayFluency = "200+";
              } else {
                displayFluency = Math.round(fluency).toString();
              }
              
              return [
                `Fluency: ${displayFluency}`,
                getBookTitle(bookId)
              ];
            } else {
              // Multiple datapoints - show weighted average and original items
              const aggregatedFluency = originalData.fluency;
              let displayAggregated;
              if (aggregatedFluency > 200) {
                displayAggregated = "200+";
              } else {
                displayAggregated = Math.round(aggregatedFluency).toString();
              }
              
              const result = [`Weighted Avg: ${displayAggregated}`];
              
              // Add each original datapoint
              originalItems.forEach((item, index) => {
                const bookId = item.bookId || 'N/A';
                const fluency = item.fluency;
                const entryCount = item.entryCount || 1;
                
                let displayFluency;
                if (fluency > 200) {
                  displayFluency = "200+";
                } else {
                  displayFluency = Math.round(fluency).toString();
                }
                
                result.push(`${displayFluency} (${entryCount}x) - ${getBookTitle(bookId)}`);
              });
              
              return result;
            }
          },
          title: function(context) {
            const dataIndex = context[0].dataIndex;
            const originalData = chartData.original[dataIndex];
            if (originalData && originalData.date) {
              const rawDate = originalData.date;
              return formatDate(rawDate);
            }
            return 'Unknown date';
          }
        }
      },
      expandedStatePlugin: {
        isExpanded: isExpanded
      }
    }
  };

  // Chart data in the format expected by react-chartjs-2
  const chartConfig = {
    labels: chartData.labels,
    datasets: [
      // Fluency data line - should be on top
      {
        label: 'Fluency',
        data: chartData.values,
        borderColor: fluencyLineColor,
        backgroundColor: 'transparent', // No background fill
        borderWidth: 2,
        fill: false, // Disable fill
        tension: 0.3,
        pointRadius: 4,
        pointBorderWidth: 1,
        pointBackgroundColor: 'white',
        pointBorderColor: fluencyLineColor,
        pointHoverRadius: 6,
        pointHoverBackgroundColor: fluencyLineColor,
        pointHoverBorderColor: 'white',
        pointHoverBorderWidth: 2
      }
    ]
  };

  // Render different content based on state
  const renderContent = () => {
    if (loading) {
      log('Rendering loading state');
      return (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100%',
          minHeight: '200px'
        }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      log('Rendering error state', error);
      return (
        <Box sx={{ p: 2 }}>
          <Typography color="error" variant="body2">
            Failed to load fluency data: {error}
          </Typography>
        </Box>
      );
    }

    if (!data || data.length === 0 || chartData.labels.length === 0) {
      log('Rendering empty state');
      return (
        <Typography 
          variant="body1" 
          align="center" 
          sx={{ marginTop: '80px', color: 'text.secondary' }}
        >
          No fluency data available
        </Typography>
      );
    }

    log('Rendering chart with', chartData.labels.length, 'data points');
    
    // Create chart options with expanded state property
    const chartOptions = { 
      ...options,
      plugins: {
        ...options.plugins,
        expandedStatePlugin: {
          isExpanded: isExpanded
        }
      }
    };
    
    // Define custom plugins for background color bands and title
    const plugins = [
      {
        id: 'customBackgroundBands',
        beforeDraw: (chart) => {
          // Read the expanded state from the chart options
          const chartIsExpanded = chart.options && 
                                  chart.options.plugins && 
                                  chart.options.plugins.expandedStatePlugin && 
                                  chart.options.plugins.expandedStatePlugin.isExpanded || false;
          
          const {ctx, chartArea, scales} = chart;
          if (!chartArea) return;
          
          const yScale = scales.y;
          
          // Clear the entire chart area first
          ctx.fillStyle = 'white';
          ctx.fillRect(chartArea.left, chartArea.top, chartArea.width, chartArea.bottom - chartArea.top);
          
          // Only draw colored bands if expanded
          if (chartIsExpanded) {
            // Draw yellow band (0 to 50)
            ctx.fillStyle = chartBandYellow;
            ctx.fillRect(
              chartArea.left, 
              yScale.getPixelForValue(thresholdYellowUpperLimit), // From 50
              chartArea.width,
              yScale.getPixelForValue(0) - yScale.getPixelForValue(thresholdYellowUpperLimit) // To 0
            );
            
            // Draw blue band (50 to 100)
            ctx.fillStyle = chartBandBlue;
            ctx.fillRect(
              chartArea.left,
              yScale.getPixelForValue(thresholdGreenLowerLimit), // From 100
              chartArea.width,
              yScale.getPixelForValue(thresholdBlueLowerLimit) - yScale.getPixelForValue(thresholdGreenLowerLimit) // From 50 to 100
            );
            
            // Draw green band (100 to 200)
            ctx.fillStyle = chartBandGreen;
            ctx.fillRect(
              chartArea.left,
              chartArea.top, // From top of chart
              chartArea.width,
              yScale.getPixelForValue(thresholdGreenLowerLimit) - chartArea.top // To 100
            );
          }
        }
      }
    ];
    
    return (
      <Box 
        ref={chartContainerRef}
        sx={{ 
          height: '270px',
          width: '100%', 
          position: 'relative',
          maxHeight: '270px',
          overflow: isCollapsed ? 'hidden' : 'visible',
          marginBottom: '10px',
          transition: 'all 0.5s ease'
        }}
        className="chart-container"
      >
        <Line 
          options={chartOptions} 
          data={chartConfig} 
          fallbackContent={<Typography>Chart could not be rendered</Typography>}
          plugins={plugins}
        />
      </Box>
    );
  };

  return (
    <Card 
      className={`${classes.card} ${isCollapsed ? classes.cardCollapsed : ''} fluency-card ${isExpanded ? 'expanded' : ''} ${isCollapsed ? 'collapsed' : ''}`}
    >
      <CardHeader 
        title={
          isCollapsed ? (
            "Fluency"
          ) : (
            <Box component="span" className={classes.titleContainer}>
              Fluency <Typography component="span" variant="subtitle2">(WCPM)</Typography>
            </Box>
          )
        } 
        subheader={!loading && !error && data && data.length > 0 && !isCollapsed ? `Avg: ${getAverageFluency()}` : null}
        className={isCollapsed ? classes.cardHeaderCollapsed : classes.cardHeader}
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Level filter toggle - only show if dashboard is enabled */}
            {student.isDashboardEnabled === 'Y' && !isCollapsed && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginRight: '8px' }}>
                <Typography variant="caption" style={{ fontSize: '0.75rem', marginTop: '5px', marginBottom: '2px', textAlign: 'center' }}>
                  {showLeveledData ? `Showing ${student.currentBuddyBooksLevel} Data` : 'Showing All Data'}
                </Typography>
                <Switch
                  checked={showLeveledData}
                  onChange={(e) => setShowLeveledData(e.target.checked)}
                  size="small"
                  color="primary"
                  style={{ margin: 0, padding: 0 }}
                />
              </Box>
            )}
            {isExpanded && (
              <IconButton 
                onClick={onExpand} 
                aria-label="Return to default view"
                title="Return to default view"
                size="small"
                className={classes.exitIcon}
              >
                <FullscreenExitIcon fontSize="small" />
              </IconButton>
            )}
            {!isExpanded && (
              <IconButton 
                onClick={onExpand} 
                aria-label="Expand"
                title="Expand"
                size="small"
                className={`${classes.expandIcon} ${isCollapsed ? classes.expandIconCollapsed : ''}`}
              >
                <FullscreenIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        }
      />
      <CardContent sx={{ 
        padding: '8px 20px 20px',
        height: 'calc(100% - 45px)',
        maxHeight: 'calc(100% - 45px)',
        overflow: 'hidden',
        paddingBottom: '20px !important',
        display: isCollapsed ? 'none' : 'block',
        transition: 'opacity 0.3s ease'
      }}>
        {renderContent()}
      </CardContent>
    </Card>
  );
};

export default Fluency;